import { useMemoizedFn } from 'ahooks';
import { Badge, Popover } from 'antd';
import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { Icons } from 'components';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import RevxJobImg from 'components/RevxJobImg';
import { RevxMoney } from 'components/RevxMoney';
import { CartLineItem } from 'domains/cartDomain';
import { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import './Cart.less';
import { useDemoFeatureFlags } from '../../../hooks';

interface Props {
  orderNowIds: number[];
  isShoppingCartPage: boolean;
  cartTotal: number;
}

export const Cart = (props: Props) => {
  const { orderNowIds, isShoppingCartPage, cartTotal } = props;
  const [visible, setVisible] = useState(false);
  const cartItem = useAppSelector((item) => item.cart.cartItem);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [timeoutTag, setTimeoutTag] = useState<NodeJS.Timeout>();
  const configItem = useAppSelector((state) => state.jobConfig.configItem);
  const location = useLocation();
  const inStartJobconfig = useMemo(() => {
    return location.pathname.includes('/job-config/') && Boolean(configItem);
  }, [location.pathname, configItem]);
  // const text = <span>Title</span>;
  useEffect(() => {
    if (history.location.pathname === routesMap.shoppingCart.path) {
      setVisible(false);
    }
  }, [history.location.pathname]);
  const defaultPopover = useMemoizedFn(() => {
    if (Boolean(cartItem)) {
      setVisible(true);
      const tag = setTimeout(async () => {
        setVisible(false);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        dispatch.cart.clearCartItem();
      }, 2500);
      setTimeoutTag(tag);
    }
  });
  useEffect(() => {
    defaultPopover();
  }, [cartItem, dispatch.cart, defaultPopover]);
  return (
    <>
      <Popover
        placement={inStartJobconfig ? 'bottomRight' : 'bottom'}
        // title={text}
        content={
          <div
            onMouseOver={() => {
              if (timeoutTag) {
                clearTimeout(timeoutTag);
              }
            }}
            onMouseLeave={() => {
              defaultPopover();
            }}
          >
            {cartItem ? (
              <CartTipRender cartItem={cartItem} orderNowIds={orderNowIds} />
            ) : (
              <CartSummerRender
                cartTotal={cartTotal}
                orderNowIds={orderNowIds}
              />
            )}
          </div>
        }
        visible={visible}
        overlayClassName={`cart-popver ${
          cartItem ? 'overlay-tip' : 'overlay-summary'
        }`}
        onVisibleChange={(visible) => {
          if (history.location.pathname === routesMap.shoppingCart.path) {
            setVisible(false);
          } else {
            setVisible(visible);
          }
        }}
      >
        <Link
          to={routesMap.shoppingCart.path}
          style={{ padding: '5.5px 14px 5px 16px' }}
          className={classNames({
            active: isShoppingCartPage,
          })}
        >
          <Badge count={orderNowIds.length} size="small" offset={[4, 0]}>
            <div className="app-header-right-cart">
              <Icons.CartIcon />
            </div>
          </Badge>
        </Link>
      </Popover>
    </>
  );
};

interface ICartTipRenderProps {
  cartItem: CartLineItem;
  orderNowIds: number[];
}

export const CartTipRender = (props: ICartTipRenderProps) => {
  const { cartItem, orderNowIds } = props;
  const history = useHistory();
  const { transformValue } = useDemoFeatureFlags();

  return (
    <div className="cart-popver-tip">
      <RevxFlexLayout
        justify="justify-center"
        className="cart-popver-tip-header"
      >
        Item added to shopping bag
      </RevxFlexLayout>

      <RevxFlexLayout align="start" className="cart-popver-tip-body">
        <div>
          <RevxJobImg image={cartItem.image} />
          <p>
            <Link
              to={routesMap.shoppingCart.path}
            >{`View all ${orderNowIds.length} items`}</Link>
          </p>
        </div>
        <div>
          <span>{cartItem.jobName}</span>
          <p>QTY: {cartItem.quantity}</p>
        </div>
      </RevxFlexLayout>

      <RevxFlexLayout
        justify="space-between"
        className="cart-popver-tip-footer"
      >
        <RevxFlexLayout className="cart-popver-tip-footer-total">
          <span>Total: </span>
          <span>
            {transformValue(
              <RevxMoney value={cartItem?.costLineItems.totalCharge} />
            )}
          </span>
        </RevxFlexLayout>
        <RevxButton
          type="primary"
          brandGreen
          size="small"
          onClick={() => {
            history.push(routesMap.shoppingCart.path);
          }}
        >
          view cart
        </RevxButton>
      </RevxFlexLayout>
    </div>
  );
};

interface ICartSummerRenderProps {
  cartTotal: number;
  orderNowIds: number[];
}

export const CartSummerRender = (props: ICartSummerRenderProps) => {
  const { cartTotal, orderNowIds } = props;
  const history = useHistory();
  const { transformValue } = useDemoFeatureFlags();
  return (
    <div className="cart-popver-summer">
      <RevxFlexLayout
        justify="justify-start"
        className="cart-popver-summer-header"
      >
        <Link
          to={routesMap.shoppingCart.path}
        >{`View all ${orderNowIds.length} items`}</Link>
      </RevxFlexLayout>
      <RevxFlexLayout
        justify="space-between"
        className="cart-popver-tip-footer"
      >
        <RevxFlexLayout className="cart-popver-tip-footer-total">
          <span>Total: </span>
          <span>{transformValue(<RevxMoney value={cartTotal} />)}</span>
        </RevxFlexLayout>

        <RevxButton
          type="primary"
          brandGreen
          size="small"
          onClick={() => {
            history.push(routesMap.shoppingCart.path);
          }}
        >
          view cart
        </RevxButton>
      </RevxFlexLayout>
    </div>
  );
};
